import { assertUnreachable } from "@lib/utils/assertUnreachable";
import type { RemovedAction, StampableAction } from "./actions";

// Converts historical/removed actions into the current reducer schema.
//
// Design rule:
// - If a removed action still carries meaningful state, translate it to an
//   equivalent current action.
// - If it is obsolete and has no effect in the current model, translate to NOOP.
export default function upcastAction(
  removedAction: RemovedAction,
): StampableAction {
  switch (removedAction.type) {
    case "EVALUATION_STATUS_CHANGE": {
      const { evaluatorId, evaluableId, change, deferredTo, legacy } =
        removedAction;
      if (change === "hide" || change === "unhide") {
        return {
          type: "EVALUATION_SET_HIDDEN",
          evaluatorId,
          evaluableId,
          hidden: change === "hide",
        };
      } else {
        return {
          type: "EVALUATION_STATE_CHANGE",
          evaluatorId,
          evaluableId,
          change,
          deferredTo,
          legacy,
        };
      }
    }
    case "ODSR_MASTER_ADJUSTMENT_CHANGE":
    case "SET_EXPECTED_MATCHING_FRACTION":
    case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE":
      return { type: "NOOP" };
    default:
      assertUnreachable(removedAction);
  }
}

type CreateRoundAction = Extract<StampableAction, { type: "CREATE_ROUND" }>;
type CreateRoundCurrent = Omit<CreateRoundAction, "version"> & {
  version: 3;
};

// CREATE_ROUND history:
//   v1  no identifier; every SFC round of that era was the 2025 round, so the
//       upcast fills in "2025".
//   v2  required a round-level identifier for SFC rounds.
//   v3  the identifier lives on the proposal form (ADD_PROPOSAL_FORM). A
//       round-level value is only ever the legacy fallback carried up from
//       v1/v2, so v2 → v3 keeps whatever it had and changes nothing else.
export function upcastCurrentAction(action: StampableAction): StampableAction {
  if (action.type !== "CREATE_ROUND") {
    return action;
  }

  if (action.version === 3) {
    return action;
  }

  const isSfcRound = action.isSfcRound !== false;
  const trimmedRoundIdentifier = action.roundIdentifier?.trim();
  const legacyRoundIdentifier =
    action.version === 2
      ? trimmedRoundIdentifier
      : (trimmedRoundIdentifier ?? "2025");

  const createRoundV3Payload: CreateRoundCurrent = {
    ...action,
    version: 3,
    roundIdentifier: isSfcRound ? legacyRoundIdentifier : undefined,
  };

  return createRoundV3Payload as StampableAction;
}
